import { useState, useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { encodeFunctionData } from 'viem';
import { signPreparedCalls } from '@account-kit/wallet-client';
import { useTranslation } from 'react-i18next';
import { getChainId } from '@wagmi/core';
import {
  userPublicClient,
  userSmartAccount,
  userSmartAccountClient,
} from '../../RecoilAtom';
import { useSessionPermissions } from '../../../hooks/checkSessionPermissions';
import { useCheckAllowance } from '../../../hooks/dataSender/Allowance';
import Platformfee from '../../../hooks/dataFetcher/platformFee';
import { convertToWeiEthers, getPoolAddressFromReceipt, getTxHashAndReceipt, handleCheckSession, sendGasFeeAsUsdt, isSolvencyGateError } from '../../../utils/helpers';
import {
  BIG_NUMBER_APPROVAL,
  DISPUTE_INITIAL_TIMER,
  NEW_FACTORY_CONTRACT,
  RAIN_ARBI_CONTRACT_ADDRESS,
  platform_chainId,
  usdr_token,
} from '../../../utils/Enviroment';
import {
  CREATE_MARKET_TIME_BUFFER,
  TIMEOUT,
  TOKEN_NAME_TO_CONTRACT_ADDRESS,
  TOKEN_NAME_TO_DECIMALS,
  TOKEN_RAIN,
  ZERO_ADDRESS,
} from '../../../utils/constants';
import CreatePoolAbi from '../../../utils/CreatePoolAbi.json';
import BalanceOfAbi from '../../../utils/BalanceOfAbi.json';
import {
  checkTokenExpiration,
  getCreatedPool,
  postCreatePoolFallBack,
  tokenToDollarConvertion,
  uploadToIpfs,
} from '../../../utils/services';
import { normalizeOutcomesForContract, outcomesToBarValues } from '../helpers';
import { notifyError } from '../../Toast';
import { DEFAULT_ERROR_HEADING } from '../../../utils/ConstantMessages';
import { config } from '../../../config/wagmi';
import { saveIndexKey } from '../../indexDb';

export const useMarketDeployment = ({
  marketData,
  updateMarketData,
  finishDeploy,
  gasFeeInUsdt,
  onSolvencyError,
}) => {
  const { t } = useTranslation();
  const [startProgressBar, setStartProgressBar] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [createdPoolData, setCreatedPoolData] = useState(null);

  const smartAccount = useRecoilValue(userSmartAccount);
  const smartAccountClient = useRecoilValue(userSmartAccountClient);
  const publicClient = useRecoilValue(userPublicClient);

  const { grantPermissions } = useSessionPermissions();
  const { getAllowanceByAddress } = useCheckAllowance();
  const { PercentReturnCalculation } = Platformfee();

  // ─── Oracle Fees ────────────────────────────────────
  const [oracleFeeState, setOracleFeeState] = useState(0);
  const [rainPriceToUsdt, setRainPriceToUsdt] = useState(0);

  const getOracleFees = useCallback(
    async (currency = 'usdr') => {
      try {
        const onlyOracleFeesNeeded = true;
        const oracleFeesResponse = await PercentReturnCalculation(
          NEW_FACTORY_CONTRACT,
          onlyOracleFeesNeeded
        );
        const isRain = currency.toUpperCase() === TOKEN_RAIN;
        let currentRainPrice = rainPriceToUsdt;
        if (isRain && currentRainPrice <= 0) {
          try {
            currentRainPrice = await tokenToDollarConvertion(TOKEN_RAIN);
            setRainPriceToUsdt(currentRainPrice);
          } catch {
            currentRainPrice = 0;
          }
        }
        const oracleFees =
          isRain && currentRainPrice > 0
            ? +oracleFeesResponse / +currentRainPrice
            : oracleFeesResponse;
        setOracleFeeState(oracleFees);
        return oracleFeesResponse;
      } catch {
        return 0;
      }
    },
    [PercentReturnCalculation, rainPriceToUsdt]
  );

  // ─── Execute on-chain pool creation ─────────────────
  const executeCreatePool = useCallback(
    async (contractParams, permissions, sessionKey) => {
      const usdtAllowance = await getAllowanceByAddress(usdr_token, NEW_FACTORY_CONTRACT);
      const rainAllowance = await getAllowanceByAddress(RAIN_ARBI_CONTRACT_ADDRESS, NEW_FACTORY_CONTRACT);
      const isUsdtApprovalNeeded = +usdtAllowance < +contractParams[9];
      const isRainApprovalNeeded = +rainAllowance < +contractParams[9];

      const callArgs = [];
      if (isUsdtApprovalNeeded) {
        callArgs.push({
          to: usdr_token,
          data: encodeFunctionData({
            abi: BalanceOfAbi,
            functionName: 'approve',
            args: [NEW_FACTORY_CONTRACT, BIG_NUMBER_APPROVAL],
          }),
        });
      }
      if (isRainApprovalNeeded) {
        callArgs.push({
          to: RAIN_ARBI_CONTRACT_ADDRESS,
          data: encodeFunctionData({
            abi: BalanceOfAbi,
            functionName: 'approve',
            args: [NEW_FACTORY_CONTRACT, BIG_NUMBER_APPROVAL],
          }),
        });
      }
      callArgs.push({
        to: NEW_FACTORY_CONTRACT,
        data: encodeFunctionData({
          abi: CreatePoolAbi,
          functionName: 'createPool',
          args: [contractParams],
        }),
      });
      callArgs.push({
        to: usdr_token,
        data: sendGasFeeAsUsdt(gasFeeInUsdt),
      });

      const preparedCalls = await smartAccountClient.prepareCalls({
        calls: callArgs,
        from: smartAccount,
        capabilities: { permissions },
      });

      const signedCalls = await signPreparedCalls(sessionKey, preparedCalls);
      const sendResult = await smartAccountClient.sendPreparedCalls({
        ...signedCalls,
        capabilities: { permissions },
      });

      const { preparedCallIds } = sendResult;
      const { txHash, receipt } = await getTxHashAndReceipt({
        preparedCallId: preparedCallIds[0],
        smartAccountClient,
        publicClient,
      });

      const poolAddress = await getPoolAddressFromReceipt(receipt, CreatePoolAbi);
      return { poolAddress, txHash };
    },
    [smartAccount, smartAccountClient, publicClient, gasFeeInUsdt, getAllowanceByAddress]
  );

  // ─── Handle session + retry logic ───────────────────
  const handleCreatePool = useCallback(
    async (contractParams) => {
      try {
        const { userPermissions: permissions, userSessionKey: sessionKey } =
          await grantPermissions();
        return await executeCreatePool(contractParams, permissions, sessionKey);
      } catch (error) {
        const isSessionError =
          error?.message?.includes?.('Session not found') ||
          error?.details?.includes?.('Session not found') ||
          String(error).includes('Session not found');

        if (isSessionError) {
          try {
            await saveIndexKey('sessionKeyData', null);
            localStorage.removeItem('sessionExpiryTime');
            const { userPermissions: freshPermissions, userSessionKey: freshSessionKey } =
              await grantPermissions();
            return await executeCreatePool(contractParams, freshPermissions, freshSessionKey);
          } catch (retryError) {
            setStartProgressBar(false);
            setIsButtonDisabled(false);
            throw retryError;
          }
        }
        setStartProgressBar(false);
        setIsButtonDisabled(false);
        throw error;
      }
    },
    [grantPermissions, executeCreatePool]
  );

  // ─── Poll for pool data after creation ──────────────
  const getPoolInfo = useCallback(
    (poolAddress) => {
      return new Promise((resolve) => {
        let retries = 0;
        const maxRetries = 15;
        const retryDelay = 800;

        const poll = async () => {
          try {
            const createdPoolRes = await getCreatedPool(poolAddress);
            if (createdPoolRes) {
              const hasData = createdPoolRes.totalVolume > 0 || createdPoolRes.totalLiquidity > 0;
              if (!hasData) {
                retries++;
                if (retries >= maxRetries) {
                  clearInterval(interval);
                  resolve();
                }
                return;
              }
              setStartProgressBar(false);
              clearInterval(interval);
              setTimeout(() => {
                updateMarketData({
                  poolContractAddress: poolAddress,
                  poolId: createdPoolRes._id,
                  txHash: createdPoolRes.txHash ?? marketData.txHash,
                  question: createdPoolRes.question ?? marketData.question,
                  description: createdPoolRes.poolDescription ?? marketData.description,
                  accessCode: createdPoolRes.accessCode ?? null,
                  isPublic: !(createdPoolRes.isPrivate ?? !marketData.isPublic),
                  token: createdPoolRes.token ?? null,
                  outcomes: createdPoolRes.options?.length
                    ? createdPoolRes.options.map((opt, i) => ({
                        id: String(i + 1),
                        label: opt.optionName ?? opt,
                        probability: opt.percentage ?? marketData.outcomes?.[i]?.probability ?? 0,
                        color: marketData.outcomes?.[i]?.color ?? '',
                      }))
                    : marketData.outcomes,
                });
                setCreatedPoolData(createdPoolRes);
                setIsButtonDisabled(false);
                finishDeploy();
                resolve();
              }, TIMEOUT);
            }
          } catch (error) {
            if (error?.response?.status === 404) {
              retries++;
              if (retries >= maxRetries) {
                clearInterval(interval);
                resolve();
              }
            } else {
              clearInterval(interval);
              resolve();
            }
          }
        };
        const interval = setInterval(poll, retryDelay);
      });
    },
    [marketData, updateMarketData, finishDeploy]
  );

  // ─── Fallback pool creation ─────────────────────────
  const createPoolFallBack = useCallback(
    async (payload) => {
      let attempts = 0;
      const maxAttempts = 20;
      const delayMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
      while (attempts < maxAttempts) {
        try {
          const createdPoolRes = await postCreatePoolFallBack(payload);
          if (createdPoolRes) {
            await getPoolInfo(createdPoolRes?.data?.data?.contractAddress);
            return;
          }
          throw new Error('No poolAddress found in response');
        } catch (error) {
          attempts++;
          if (attempts >= maxAttempts) {
            notifyError(DEFAULT_ERROR_HEADING, t('Market Creation Failed. Please try again'));
            setStartProgressBar(false);
            break;
          }
          await delayMs(5000);
        }
      }
    },
    [getPoolInfo, t]
  );

  // ─── Main confirm handler ──────────────────────────
  const handleMarketConfirm = useCallback(async () => {
    const chainId = getChainId(config);
    if (chainId !== platform_chainId) {
      notifyError(DEFAULT_ERROR_HEADING, t('Please connect to the Arbitrum network'));
      return;
    }

    setIsButtonDisabled(true);
    setStartProgressBar(true);

    const hasScheduledStart = marketData.startDate && new Date(marketData.startDate).getTime() > Date.now();
    const startDateObj = hasScheduledStart
      ? new Date(marketData.startDate)
      : new Date(Date.now() + CREATE_MARKET_TIME_BUFFER);
    const startTime = Math.floor(startDateObj.getTime() / 1000);
    const endTime = Math.floor(new Date(marketData.resolutionDate).getTime() / 1000);
    const formattedStartDate = startDateObj.toISOString();
    const formattedEndDate = new Date(marketData.resolutionDate).toISOString();
    const currency = (marketData.currency || 'USDR').toUpperCase();
    let execOutcomes = null;

    try {
      const isExpired = await checkTokenExpiration();
      if (isExpired) {
        notifyError(DEFAULT_ERROR_HEADING, t('Session expired. Please log in again.'));
        setStartProgressBar(false);
        setIsButtonDisabled(false);
        return;
      }

      const cleanedOutcomes = (marketData.outcomes ?? []).filter(
        (o) => o.isUnclassified || (o.label && o.label.trim() !== '')
      );
      if (cleanedOutcomes.length !== (marketData.outcomes ?? []).length) {
        updateMarketData({ outcomes: cleanedOutcomes });
      }

      execOutcomes = normalizeOutcomesForContract(cleanedOutcomes);
      const barValues = outcomesToBarValues(execOutcomes);

      const metadata = {
        question: marketData.question,
        options: execOutcomes.map((o) => o.label),
        tags: marketData.category ? [marketData.category] : ['Other'],
        isPrivate: !marketData.isPublic,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        poolDescription: marketData.description,
        isAiResolver: marketData.isPublic && marketData.resolutionMethod === 'ai',
        contractData: {
          pool_owner: smartAccount,
          start_time: startTime,
          end_time: endTime,
          no_of_options: execOutcomes.length,
        },
      };

      const ipfsUrl = await uploadToIpfs(metadata);
      if (!ipfsUrl) {
        notifyError(DEFAULT_ERROR_HEADING, t('Failed to upload to IPFS.'));
        setStartProgressBar(false);
        setIsButtonDisabled(false);
        return;
      }

      const effectiveOracleFee = oracleFeeState;
      const totalLiquidity = Math.max(Number(marketData.liquidity) - effectiveOracleFee, 0);
      const inputAmountWei = totalLiquidity
        ? convertToWeiEthers(totalLiquidity, TOKEN_NAME_TO_DECIMALS[currency])
        : '0';
      await handleCheckSession();

      const contractParams = [
        marketData.isPublic,
        metadata.isAiResolver,
        smartAccount,
        ZERO_ADDRESS,
        startTime,
        endTime,
        execOutcomes.length,
        DISPUTE_INITIAL_TIMER,
        ipfsUrl,
        inputAmountWei,
        barValues,
        smartAccount,
        TOKEN_NAME_TO_CONTRACT_ADDRESS[currency],
      ];

      const result = await handleCreatePool(contractParams);
      if (!result) return;
      const { poolAddress, txHash } = result;
      if (poolAddress && txHash) {
        await getPoolInfo(poolAddress);
      }
    } catch (error) {
      if (isSolvencyGateError(error)) {
        onSolvencyError?.();
        setStartProgressBar(false);
        setIsButtonDisabled(false);
        return;
      }
      try {
        const payload = {
          question: marketData.question,
          isPrivate: !marketData.isPublic,
          ...(marketData.description?.trim() && { poolDescription: marketData.description }),
          options: execOutcomes
            ? execOutcomes.map((o) => ({ optionName: o.label }))
            : (marketData.outcomes ?? [])
                .filter(
                  (o) => o.isUnclassified || (o.label && o.label.trim() !== '')
                )
                .map((o) => ({ optionName: o.label })),
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          tags: marketData.category ? [marketData.category] : ['Other'],
        };
        await createPoolFallBack(payload);
      } catch (fbError) {
        notifyError(DEFAULT_ERROR_HEADING, t('Market Creation Failed. Please try again'));
        setStartProgressBar(false);
        setIsButtonDisabled(false);
      }
    } finally {
      // isButtonDisabled is reset inside getPoolInfo/createPoolFallBack after success popup opens
    }
  }, [
    marketData,
    smartAccount,
    gasFeeInUsdt,
    oracleFeeState,
    handleCreatePool,
    getPoolInfo,
    createPoolFallBack,
    updateMarketData,
    t,
  ]);

  return {
    handleMarketConfirm,
    startProgressBar,
    setStartProgressBar,
    isButtonDisabled,
    setIsButtonDisabled,
    createdPoolData,
    getOracleFees,
    oracleFixedFee: oracleFeeState,
  };
};
